import { z, ZodType } from 'zod'

import { AccomodationEnum } from '../../../lib/types/enums/accomodation-type/accomodation-type'
import { JourneyPlace } from '../../../lib/types/models/journey-place/journey-place'
import { JourneyPlaceInput } from '../../../lib/types/models/journey-place/journey-place-input'
import { OtoIndividualPlaceInput } from '../../../lib/types/models/journey-place/oto-individual-place-input'
import { createEnumSchema } from '../../../lib/utils/validations/enum-validation'
import { createNumberSchema } from '../../../lib/utils/validations/int-array-validation'
import { createStringSchema } from '../../../lib/utils/validations/string-validation'

export const COLLECTIVE_ACCOMODATION_TYPES: AccomodationEnum[] = [
  AccomodationEnum.COLLEGE,
  AccomodationEnum.CAMPUS,
  AccomodationEnum.RESIDENCE,
  AccomodationEnum.HOTEL
]

export const journeyPlaceFormSchema: ZodType<JourneyPlaceInput> = z
  .object({
    name: createStringSchema('NOM', { nullish: true }),
    address: createStringSchema('Adresse', { nullish: true }),
    countryId: createNumberSchema('pays', { nullish: true }),
    accomodation: createEnumSchema({
      enumType: AccomodationEnum,
      fieldName: "type d'hébergement",
      nullish: true
    }),
    timezone: createStringSchema('Timezone', { nullish: true }),
    isOtoIndividual: z.boolean().default(false),
    price: createNumberSchema('Prix', { nullish: true }),
    hasAgeLimit: z.boolean().default(false),
    mandatoryUnderAge: createNumberSchema('Âge', { isInteger: true, nullish: true })
  })
  .superRefine((data, ctx) => {
    if (!data.name?.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Veuillez entrer un nom de lieu de séjour',
        path: ['name']
      })
    }

    // Validate countryId (always required)
    if (!data.countryId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Veuillez sélectionner un pays',
        path: ['countryId']
      })
    }

    // Validate accomodation (always required)
    if (!data.accomodation) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Veuillez sélectionner un type d'hébergement",
        path: ['accomodation']
      })
    }

    if (COLLECTIVE_ACCOMODATION_TYPES.includes(data.accomodation) && !data.address?.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Adresse est requis pour ce type d'hébergement",
        path: ['address']
      })
    }

    // Âge is required when the age limit is enabled on an OTO Individual place
    if (data.isOtoIndividual && data.hasAgeLimit && !data.mandatoryUnderAge) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Veuillez entrer un âge',
        path: ['mandatoryUnderAge']
      })
    }

    // Prix is required on an OTO Individual place
    if (data.isOtoIndividual && data.price == null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Veuillez entrer un prix',
        path: ['price']
      })
    }
  })

export const OTO_INDIVIDUAL_PLACE_FORM_SCHEMA_VALIDATION: ZodType<OtoIndividualPlaceInput> =
  z.object({
    title: createStringSchema('Intitulé'),
    code: createStringSchema('Code IATA')
  })

export const mapJourneyPlaceToInput = (journeyPlace: JourneyPlace): JourneyPlaceInput => ({
  name: journeyPlace.name,
  accomodation: journeyPlace.accomodation,
  countryId: journeyPlace.countryId,
  address: journeyPlace.address,
  timezone: journeyPlace.timezone,
  isOtoIndividual: journeyPlace.isOtoIndividual,
  price: journeyPlace.price,
  hasAgeLimit: journeyPlace.hasAgeLimit,
  mandatoryUnderAge: journeyPlace.mandatoryUnderAge
})
